import { Link } from "react-router-dom";

import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import ArticleLayout from "./ArticleLayout";

export default function BonFiscalVsFacturaPage() {
  return (
    <>
      <Seo {...seoFromPublicPage("/bon-fiscal-vs-factura-combustibil")} />
      <ArticleLayout
        eyebrow="Documente justificative"
        title="Bon fiscal vs factură la combustibil: ce acceptă ANAF"
        lead="Bonul fiscal e suficient la pompă. Factura e mai sigură la control. Diferența contează când vrei deducere 100% și inspectorul cere corelația dintre documente, foaie și alimentări."
      >
        <p>
          Când alimentezi mașina de firmă, primești bon fiscal, factură sau amândouă.
          Toate trei funcționează — dar nu la fel. La <strong>50%</strong>, bonul e de
          obicei suficient. La <strong>100%</strong>, factura îți dă un fir clar între
          CUI, vehicul și foaia de parcurs.
        </p>

        <h2>Ce acceptă ANAF ca document justificativ</h2>
        <p>
          Legislația nu interzice bonul fiscal. Dar la control, inspectorul vrea să vadă
          că alimentarea aparține vehiculului din foaia de parcurs și firmei care deduce.
          Bonul are mai puține date; factura le are pe toate.
        </p>
        <div className="article-doc__table-wrap">
          <table className="article-doc__table">
            <thead>
              <tr>
                <th>Element</th>
                <th>Bon fiscal</th>
                <th>Factură</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>CUI-ul firmei tale</td>
                <td>Uneori (dacă îl ceri)</td>
                <td>Da, obligatoriu</td>
              </tr>
              <tr>
                <td>Număr de înmatriculare</td>
                <td>Rar (depinde de stație)</td>
                <td>Uzual (dacă îl ceri)</td>
              </tr>
              <tr>
                <td>Tip carburant și litri</td>
                <td>Da</td>
                <td>Da</td>
              </tr>
              <tr>
                <td>Dată și oră exactă</td>
                <td>Da</td>
                <td>Da</td>
              </tr>
              <tr>
                <td>TVA defalcat</td>
                <td>Nu întotdeauna</td>
                <td>Da, obligatoriu</td>
              </tr>
              <tr>
                <td>Legătură directă cu vehiculul</td>
                <td>Slab</td>
                <td>Clar</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Dacă ai bon fără CUI și fără număr de mașină, inspectorul poate întreba: de
          unde știu că alimentarea asta e pentru vehiculul din foaie, nu pentru mașina
          personală?
        </p>

        <h2>La 50%, bonul e de obicei suficient</h2>
        <p>
          Când aplici regimul de <strong>50%</strong>, ai recunoscut deja că mașina e
          folosită și personal. Inspectorul nu caută potrivire perfectă între fiecare
          bon și fiecare cursă. Un dosar cu bonuri, o foaie rezonabilă și cheltuieli
          proporționale cu km parcurși — de cele mai multe ori, ajunge.
        </p>
        <p>
          Totuși, chiar și la 50%, dacă bonurile nu au CUI și firma deduce TVA, riscul
          crește. TVA-ul se recuperează pe baza unui document fiscal valid, nu pe baza
          unui bon anonim.
        </p>

        <h2>La 100%, factura e mai sigură</h2>
        <p>
          La <strong>deducere 100%</strong>, totul trebuie să se lege: foaia de parcurs,
          alimentările, regulamentul intern și dovezile de plată. Factura are CUI-ul
          firmei, poate include numărul de înmatriculare și arată clar TVA-ul defalcat.
        </p>
        <p>
          Dacă foaia spune că mașina TM-01-ABC a parcurs 1.200 km în luna mai, iar tu ai
          trei facturi cu același număr de mașină și litri care se potrivesc cu consumul,
          inspectorul vede coerență. Dacă ai doar bonuri fără identificare, legătura e
          presupusă, nu demonstrată.
        </p>
        <p>
          Condițiile complete pentru 100% sunt în{" "}
          <Link to="/deducere-100-combustibil">articolul despre deducerea integrală</Link>.
          Factura nu e singura piesă, dar e cea care face legătura vizibilă.
        </p>

        <h2>Ce faci când ai doar bon fiscal</h2>
        <p>
          Dacă stația nu emite factură sau nu ai cerut-o, bonul rămâne document
          justificativ — dar trebuie să compensezi lipsa de date:
        </p>
        <ul>
          <li>
            <strong>Cere bon cu CUI.</strong> Majoritatea caselor de marcat permit
            introducerea CUI-ului înainte de emitere. Bonul devine „bon fiscal cu date
            de identificare".
          </li>
          <li>
            <strong>Notează pe bon numărul mașinii.</strong> Nu e obligatoriu legal, dar
            la control arată intenție de evidență.
          </li>
          <li>
            <strong>Păstrează bonurile cronologic, alături de foaie.</strong> Dacă
            bonul din 15 mai se potrivește cu alimentarea din foaie din aceeași zi,
            legătura e mai ușor de văzut.
          </li>
        </ul>
        <p>
          Bonul fără CUI nu e automat refuzat, dar la 100% ridică întrebări pe care
          factura le evită din start.
        </p>

        <h2>Ce cade la control</h2>
        <p>
          Inspectorul nu respinge bonurile din principiu. Respinge lipsa de legătură
          dintre cheltuială și activitatea economică.
        </p>
        <div className="article-doc__callout article-doc__callout--warning">
          <strong>De evitat</strong>
          <ul>
            <li>Bonuri fără CUI și fără număr de mașină, la regim de 100%.</li>
            <li>Alimentări în weekend sau concediu, fără cursă pe foaie.</li>
            <li>Litri care nu se potrivesc cu km din foaie și norma de consum.</li>
            <li>Bonuri de la stații din alte orașe, fără deplasare justificată.</li>
          </ul>
        </div>
        <p>
          Coerența contează mai mult decât tipul de document. Un bon cu CUI, aliniat cu
          foaia, e mai bun decât o factură pentru o alimentare care nu apare nicăieri.
        </p>

        <h2>Card de combustibil: factură automată</h2>
        <p>
          Dacă ai card de flotă (OMV, Petrom, MOL, Rompetrol etc.), alimentările vin cu
          factură lunară sau per tranzacție, cu toate datele: CUI, număr mașină, litri,
          dată. La control, legătura e evidentă.
        </p>
        <p>
          Dacă folosești card, dar și plătești cash ocazional, asigură-te că bonurile
          cash au măcar CUI-ul firmei. Altfel, apar goluri în evidență.
        </p>

        <h2>Legătura cu foaia de parcurs</h2>
        <p>
          Documentul justificativ (bon sau factură) arată <em>cât</em> ai alimentat.
          Foaia de parcurs arată <em>unde</em> ai consumat. Cele două trebuie să se
          potrivească.
        </p>
        <p>
          Dacă în mai ai alimentat 150 litri și foaia arată 2.000 km la un consum de
          7,5 l/100 km, calculul iese. Dacă foaia arată 800 km, inspectorul întreabă
          unde au dispărut ceilalți litri.
        </p>
        <p>
          Cum stabilești norma de consum și cum o legi de alimentări e în{" "}
          <Link to="/norma-de-consum-foaie-parcurs">ghidul dedicat</Link>. Bonul sau
          factura sunt doar jumătate din poveste.
        </p>

        <h2>Concluzie</h2>
        <p>
          Bonul fiscal și factura sunt ambele documente justificative acceptate de ANAF.
          Diferența e în cât de clar demonstrează legătura dintre alimentare, vehicul și
          firmă.
        </p>
        <p>
          <strong>La 50%.</strong> Bonul e de obicei suficient, mai ales dacă are CUI.
          Nu e nevoie de potrivire perfectă, dar evidența trebuie să fie rezonabilă.
        </p>
        <p>
          <strong>La 100%.</strong> Factura e mai sigură. Are CUI, poate avea număr de
          mașină, arată TVA-ul clar. Când foaia, alimentările și factura spun același
          lucru, inspectorul nu mai are ce să întrebe.
        </p>
        <p>
          Dacă tot ești la pompă, cere factură. Durează un minut în plus și îți scutește
          explicații la control.
        </p>
      </ArticleLayout>
    </>
  );
}
